import { polygon as turfPolygon } from '@turf/helpers';
import { default as turfIntersect } from '@turf/intersect';
import isEmpty from 'lodash/isEmpty';

import { DELTA } from 'common/components/maps/constants';
import SegmentPolygonBuilder from 'common/components/maps/models/SegmentPolygonBuilder';
import SegmentRectangleBuilder from 'common/components/maps/models/SegmentRectangleBuilder';
import { dividePolygon } from 'common/components/segment/asceHelpers';
import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import {
    getSolarModulesInSegment,
    getSolarModulesInSegmentDynamicAlgorithm,
} from 'common/components/segment/solarGeometry';
import {
    getCenter,
    getPolygonPointsWithSetback,
} from 'common/components/segment/solarGeometryhelpers';

import iconMarkerColor from 'resources/iconMarkerColor.svg';

import { createFinishHandler } from './components/TerraDraw/terraDrawManager';

const vertexChangeDebounce = (checkSegment) => {
    let interval;
    return ({ evt = '', polygon, segmentId } = {}) => {
        clearTimeout(interval);
        interval = setTimeout(() => {
            interval = null;
            if (polygon.length <= 0) return;
            checkSegment({
                evt,
                polygon: polygon.map((latLng) => [latLng.lat(), latLng.lng()]),
                segmentId,
            });
        }, 250);
    };
};

export const buildShape = ({
    checkSegment,
    google,
    handleOnClickModule,
    isDisabledEdition,
    mapRef,
    newSegment,
    onContextMenu,
    prepareEditSegment,
    selectSegmentById,
    setSelectedItems,
    setTempSegments,
    sunPosition,
}) => {
    let newShape;
    const shapeCoords = newSegment.polygon.map(
        (point) => new google.maps.LatLng(point[0], point[1]),
    );
    const vertexChangeDebounceBuild = vertexChangeDebounce(checkSegment);
    const map = mapRef.current.self();

    if (
        [POLYGON_OBSTACLE, POLYGON_SEGMENT, POLYGON_CIRCLE].includes(
            newSegment.type,
        )
    ) {
        const SegmentPolygon = SegmentPolygonBuilder(
            google,
            vertexChangeDebounceBuild,
            onContextMenu,
        );

        newShape = new SegmentPolygon({
            drawingType: newSegment.drawing_type,
            height: newSegment.height || newSegment.building_height,
            id: newSegment.id,
            metadata: newSegment,
            onClickModule: handleOnClickModule,
            options: {
                draggable: true,
                editable: true,
                fillColor: newSegment.segment_color,
                fillOpacity: 0.7,
                map,
                paths: shapeCoords,
                strokeColor: newSegment.segment_stroke_color,
            },
            safeZone: newSegment.safe_zone,
            solarModules: newSegment.solar_modules,
            sunPosition,
            type: newSegment.type,
        });
    }

    if ([POLYGON_MAP, POLYGON_RECTANGLE].includes(newSegment.type)) {
        const SegmentRectangle = SegmentRectangleBuilder(
            google,
            vertexChangeDebounceBuild,
        );

        const bounds = new google.maps.LatLngBounds();
        shapeCoords.forEach((coord) => bounds.extend(coord));

        newShape = new SegmentRectangle({
            id: newSegment.id,
            image: newSegment.image,
            opacity: newSegment.opacity,
            options: {
                bounds,
                map,
                strokeColor: newSegment.segment_stroke_color,
            },
            rotate: newSegment.rotate,
            type: newSegment.type,
        });
    }

    const shapeWithEvents = initEventsShape({
        google,
        isDisabledEdition,
        newShape,
        prepareEditSegment,
        segmentId: newSegment.id,
        selectSegmentById,
        setSelectedItems,
    });

    if (isDisabledEdition) {
        shapeWithEvents.setDisabled(true);
    }

    setTempSegments((tempSegments) => ({
        ...tempSegments,
        [newSegment.id]: shapeWithEvents,
    }));
};

export const buildShapes = ({
    checkSegment,
    google,
    handleOnClickHide,
    handleOnClickModule,
    isDisabledEdition,
    mapRef,
    onContextMenu,
    prepareEditSegment,
    segments,
    selectSegmentById,
    setSelectedItems,
    setTempSegments,
    sunPosition,
    tempSegments,
}) => {
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (tempSegments[segment.id]) continue;

        buildShape({
            checkSegment,
            google,
            handleOnClickHide,
            handleOnClickModule,
            isDisabledEdition,
            mapRef,
            newSegment: segment,
            onContextMenu,
            prepareEditSegment,
            selectSegmentById,
            setSelectedItems,
            setTempSegments,
            sunPosition,
        });
    }
};

const initEventsShape = ({
    google,
    isDisabledEdition,
    newShape,
    prepareEditSegment,
    segmentId,
    selectSegmentById,
    setSelectedItems,
}) => {
    if (isDisabledEdition) return newShape;
    google.maps.event.addListener(
        newShape,
        'click',
        ({ domEvent: { ctrlKey, metaKey } }) => {
            selectSegmentById(segmentId);
            setSelectedItems({
                isMulti: ctrlKey || metaKey,
                segmentId,
            });
        },
    );
    google.maps.event.addListener(newShape, 'dblclick', () => {
        prepareEditSegment(segmentId);
    });

    return newShape;
};

export const getDictionaryById = (array) => {
    return array.reduce(
        (acc, current) => ({ ...acc, [current.id]: current }),
        {},
    );
};

/* useEffects */
export const copyPasteEventsUseEffect = ({
    commercialOfferId,
    handleOnCopy,
    handleOnPaste,
    mapValue,
    refBox,
}) => {
    return () => {
        if (!refBox || !mapValue) return;

        const handleKeyDown = (event) => {
            event.preventDefault();
            const code = event.which || event.keyCode;
            let charCode = String.fromCharCode(code).toLowerCase();
            if (event.ctrlKey || event.metaKey) {
                if (charCode === 'c') handleOnCopy();
                if (charCode === 'v') handleOnPaste({ commercialOfferId });
            }
        };

        refBox.current.addEventListener('keydown', handleKeyDown);

        return () => {
            if (!refBox) return;
            refBox.current?.removeEventListener('keydown', handleKeyDown);
        };
    };
};

export const drawingModeUseEffect = ({
    breakpoint,
    createSegment,
    drawingType,
    google,
    isDrawingMode,
    map,
    onOpenDrawer,
    localController,
}) => {
    return () => {
        if (!map || !google) {
            return;
        }

        if (!isDrawingMode) {
            if (localController) {
                localController.setMode('static');
                localController.stop();
            }
            return;
        }

        try {
            // Crear una nueva instancia de TerraDraw solo cuando entramos en modo dibujo
            localController.setDrawingMode(drawingType);

            const finishHandler = createFinishHandler({
                controller: localController,
                createSegment,
                onOpenDrawer,
                breakpoint,
            });

            localController.onFinish(finishHandler);
        } catch (error) {
            console.error('Error setting up Terra Draw mode:', error);
        }
    };
};

export const editSegmentDataUseEffect = ({ editSegmentData, tempSegments }) => {
    return () => {
        if (isEmpty(editSegmentData)) return;

        if (tempSegments[editSegmentData.id]?.setMetadata) {
            tempSegments[editSegmentData.id].setMetadata(editSegmentData);
        }

        if (editSegmentData.type !== POLYGON_MAP) {
            if (tempSegments[editSegmentData.id].setSolarModules) {
                tempSegments[editSegmentData.id].setSolarModules(
                    editSegmentData.solar_modules,
                );
            }

            if (tempSegments[editSegmentData.id].setAditionalOptions) {
                tempSegments[editSegmentData.id].setAditionalOptions({
                    fillColor: editSegmentData.segment_color,
                    fillOpacity: 0.7,
                });
            }

            if (tempSegments[editSegmentData.id].setHeight) {
                tempSegments[editSegmentData.id].setHeight(
                    editSegmentData.height,
                    editSegmentData?.zIndex || 0,
                );
            }

            if (tempSegments[editSegmentData.id].setSafeZone) {
                tempSegments[editSegmentData.id].setSafeZone(
                    editSegmentData.safe_zone,
                );
            }
        }

        if (editSegmentData.type === POLYGON_MAP) {
            tempSegments[editSegmentData.id].setOverlayOptions(
                editSegmentData.image,
                editSegmentData.opacity,
                editSegmentData.rotate,
            );
        }
    };
};

export const mapValueUseEffect = ({
    currentProjectLocation,
    google,
    mapValue,
}) => {
    let marker = null;
    let originalSetFunc = null;

    return () => {
        if (!mapValue || !currentProjectLocation || !google) return;

        const position = new google.maps.LatLng(
            currentProjectLocation.latitude,
            currentProjectLocation.longitude,
        );

        const icon = document.createElement('img');
        icon.src = iconMarkerColor;
        icon.style.height = '32px';

        marker = new google.maps.marker.AdvancedMarkerElement({
            content: icon,
            map: mapValue,
            position,
        });

        mapValue.setOptions({
            restriction: {
                latLngBounds: {
                    east: position.lng() + DELTA,
                    north: position.lat() + DELTA,
                    south: position.lat() - DELTA,
                    west: position.lng() - DELTA,
                },
                strictBounds: true,
            },
            tilt: 0,
        });

        let zoomRangeModifier = mapValue.__proto__.__proto__.__proto__;
        originalSetFunc = zoomRangeModifier.set;
        const hackedSetFunc = function (a, b) {
            if (a === 'maxZoom') b += 1;
            originalSetFunc.call(this, a, b);
        };
        zoomRangeModifier.set = hackedSetFunc;

        return () => {
            if (!mapValue) return;

            if (marker) {
                marker.setMap(null);
                marker = null;
            }

            let zoomRangeModifier = mapValue.__proto__.__proto__.__proto__;
            zoomRangeModifier.set = function (a, b) {
                originalSetFunc.call(this, a, b);
            };
        };
    };
};

const getPolygonWithSetback = ({ google, googlePolygon, setback }) => {
    if (!google || !google.maps) return;

    const newSetback = -1 * parseFloat(setback) || 0;

    const polygonPointsWithSetback = getPolygonPointsWithSetback({
        polygon: googlePolygon,
        setback: newSetback,
    });

    return new google.maps.Polygon({
        paths: polygonPointsWithSetback,
    });
};

const getNewModulesInADoubleSlopeSegment = ({
    building_heading,
    google,
    map,
    maxSolarModules,
    obstacles,
    polygonWithSetback,
    runAlgotithm,
    setup,
}) => {
    const polygonPoints = polygonWithSetback.getPath().getArray();
    const subPolygons = dividePolygon({
        google,
        heading: building_heading,
        polygonPoints,
    });

    let boxes1 = [];
    let boxes2 = [];
    let grouped1 = [];
    let grouped2 = [];

    const polygon1 = new google.maps.Polygon({ paths: subPolygons[0] });
    const resultPolygon1 = runAlgotithm({
        google,
        map,
        maxSolarModules,
        obstacles,
        polygon: polygon1,
        setup,
    });
    boxes1 = resultPolygon1.boxes;
    grouped1 = resultPolygon1.grouped;

    const parsedAzimuth = parseFloat(setup.azimuth);
    const azimuth =
        parsedAzimuth + 180 > 360 ? parsedAzimuth - 180 : parsedAzimuth + 180;
    const newMaxSolarModules = maxSolarModules - boxes1.length;
    const polygon2 = new google.maps.Polygon({ paths: subPolygons[1] });
    const resultPolygon2 = runAlgotithm({
        google,
        initialGroup: grouped1?.length || 0,
        map,
        maxSolarModules: newMaxSolarModules,
        obstacles,
        polygon: polygon2,
        setup: { ...setup, azimuth },
    });
    boxes2 = resultPolygon2.boxes;
    grouped2 = resultPolygon2.grouped;

    return {
        boxes: [...boxes1, ...boxes2],
        grouped: [...grouped1, ...grouped2],
    };
};

export const getNewModules = ({
    algorithm,
    building_heading,
    data,
    google,
    map,
    module,
    obstacles,
    sloof_type,
}) => {
    const { assigned, quantity, polygon, ...solar_characterization } = data;
    const parsedPolygon = JSON.parse(polygon);

    if (!google || !google.maps) return { boxes: [], grouped: [] };
    if (parsedPolygon.length <= 2 || isEmpty(module))
        return { boxes: [], grouped: [] };

    const { height: solarModuleHeight = 0, width: solarModuleWidth = 0 } =
        module;

    const setup = {
        azimuth: solar_characterization.azimuth,
        frameCols: solar_characterization.frame_cols_number,
        frameColSpacing: solar_characterization.frame_col_spacing,
        frameRows: solar_characterization.frame_rows_number,
        frameRowSpacing: solar_characterization.frame_row_spacing,
        height: solar_characterization.height,
        maxCols: data.maxCols,
        maxRows: data.maxRows,
        minCols: data.minCols,
        minRows: data.minRows,
        roofTilt: data.ceil_tilt || 0,
        solarModuleColSpacing: solar_characterization.col_spacing,
        solarModuleHeight: solarModuleHeight,
        solarModuleOrientation: solar_characterization.orientation,
        solarModuleRowSpacing: solar_characterization.row_spacing,
        solarModuleWidth: solarModuleWidth,
        tilt: solar_characterization.tilt,
        tolerance: solar_characterization.safe_zone,
        zIndex: solar_characterization.zIndex,
    };

    const maxSolarModules =
        parseInt(quantity) - parseInt(assigned) <= 0
            ? parseInt(quantity)
            : parseInt(quantity) - parseInt(assigned);

    const googlePolygon = new google.maps.Polygon({
        paths: parsedPolygon.map((latlng) => ({
            lat: latlng[0],
            lng: latlng[1],
        })),
    });

    const runAlgotithm =
        algorithm === 'dynamic'
            ? getSolarModulesInSegmentDynamicAlgorithm
            : getSolarModulesInSegment;

    const polygonWithSetback = getPolygonWithSetback({
        google,
        googlePolygon,
        setback: solar_characterization.safe_zone,
    });

    if (sloof_type === 'double_slope' && building_heading) {
        return getNewModulesInADoubleSlopeSegment({
            building_heading: parseFloat(building_heading),
            google,
            map,
            maxSolarModules,
            obstacles,
            polygonWithSetback,
            runAlgotithm,
            setup,
        });
    }

    const { boxes, grouped } = runAlgotithm({
        google,
        map,
        maxSolarModules,
        obstacles,
        polygon: polygonWithSetback,
        setup,
    });

    return { boxes, grouped };
};

export const getObstaceZIndex = ({
    google,
    polygon,
    segments,
    selectedSegment,
}) => {
    const center = getCenter({
        google,
        paths: polygon.map((point) => ({
            lat: point[0],
            lng: point[1],
        })),
    });

    const filteredSegmnets = segments.filter(
        (segment) => segment.id !== selectedSegment.id,
    );

    let zIndex = 0;

    if (filteredSegmnets.length > 0) {
        filteredSegmnets.forEach((segment) => {
            const temp_polygon = new google.maps.Polygon({
                paths: segment.polygon.map((point) => ({
                    lat: point[0],
                    lng: point[1],
                })),
            });

            const flag = google.maps.geometry.poly.containsLocation(
                center,
                temp_polygon,
            );

            const segmentHeight = parseFloat(segment.height);

            if (flag) {
                zIndex += segmentHeight;
            }
        });

        if (zIndex) zIndex++;
    }

    return zIndex || 0;
};

export const getCollidedPolygons = ({ points, polygons }) => {
    const collidedPolygons = [];

    const obstacleCoords = points.map((latlng) => [latlng[1], latlng[0]]);
    obstacleCoords.push(obstacleCoords[0]);

    const obstacleTurfPolygon = turfPolygon([obstacleCoords]);

    for (const polygon of polygons) {
        const segmentCoords = polygon.polygon.map((latlng) => [
            latlng[1],
            latlng[0],
        ]);
        segmentCoords.push(segmentCoords[0]);
        const segmentTurfPolygon = turfPolygon([segmentCoords]);

        try {
            const intersection = turfIntersect(
                obstacleTurfPolygon,
                segmentTurfPolygon,
            );

            if (intersection) {
                collidedPolygons.push(polygon);
            }
        } catch (error) {
            console.warn('Intersection check failed:', error);
        }
    }

    return collidedPolygons;
};

export const detectFieldChanges = (
    presetConfiguration = {},
    solutionFields = {},
) => {
    const preset = presetConfiguration || {};
    const changedFields = [];

    for (const field of Object.keys(solutionFields)) {
        const presetValue = preset[field];
        const currentValue = solutionFields[field];

        // Si el campo no existe en preset → no considerar cambio
        if (presetValue === undefined) {
            continue;
        }

        // Caso 1: valor primitivo (string, number, boolean, null)
        if (typeof presetValue !== 'object' || presetValue === null) {
            if (currentValue !== presetValue) {
                changedFields.push(field);
            }
            continue;
        }

        // Caso 2: objeto con strategy
        const presetStrategy = presetValue.strategy;
        const currentStrategy = currentValue?.strategy ?? null;

        // 2.1 - Cambio de estrategia
        if (currentStrategy !== presetStrategy) {
            changedFields.push(field);
            continue;
        }

        // 2.2 - Mismo strategy → comparar el valor correspondiente
        if (presetStrategy === 'manual') {
            const presetVal = presetValue.value;
            const currentVal = currentValue?.value;

            const arraysEqual = (a, b) => {
                if (a === b) return true;
                if (!Array.isArray(a) || !Array.isArray(b)) return false;
                if (a.length !== b.length) return false;
                return a.every((val, i) => val === b[i]);
            };

            if (!arraysEqual(currentVal, presetVal)) {
                changedFields.push(field);
            }
        } else if (presetStrategy === 'custom') {
            const presetCustom = presetValue.custom_value;
            const currentCustom = currentValue?.custom_value;

            if (currentCustom !== presetCustom) {
                changedFields.push(field);
            }
        }
        // Puedes añadir más estrategias aquí: 'automatic', 'recommended', etc.
        // Para 'automatic' normalmente no hay valor que comparar, solo la strategy
    }

    return {
        hasChanges: changedFields.length > 0,
        changedFields,
    };
};
